import type { CSSProperties } from 'react';
import type { DriftChange, DriftReport, Severity } from './api.ts';
import { EvidenceValues, REASON_LABEL, Sev, VERDICT_KEY, VERDICT_LABEL } from './Findings.tsx';
import './results.css';

/**
 * Ordering: regressions first, then the two categories a reader is most likely
 * to misread — losing sight of a control, and a difference our own rules
 * caused — then the good news, then the noise.
 */
const KIND_ORDER: readonly string[] = [
  'regression',
  'visibility_lost',
  'not_comparable',
  'scope_changed',
  'improvement',
  'visibility_gained',
  'evidence_changed',
  'control_added',
  'control_removed',
];

const KIND_LABEL: Record<string, string> = {
  regression: 'Regressed',
  improvement: 'Improved',
  visibility_lost: 'No longer visible',
  visibility_gained: 'Now visible',
  scope_changed: 'Scope changed',
  evidence_changed: 'Changed, still met',
  not_comparable: 'Not comparable',
  control_added: 'New control',
  control_removed: 'Control retired',
};

/**
 * Which family of edge each category draws, in the vocabulary of the four
 * verdicts rather than a parallel one of its own.
 *
 * `visibility_lost` is violet — the "not evidenced" family — and that is the
 * single most important line in this file. Losing sight of a control is not a
 * regression: the client did not get worse, we stopped being able to look.
 * Drawing it red would tell an MSP their customer has a new security problem
 * when what they have is an expired licence, and they would spend the service
 * review apologising for something that did not happen.
 *
 * `evidence_changed` is amber, not a verdict colour at all: the verdict is
 * still Met, and the reader is being asked to notice movement rather than to
 * treat it as failure.
 */
const KIND_RAIL: Record<string, string> = {
  regression: 'not-met',
  improvement: 'met',
  visibility_lost: 'not-evidenced',
  visibility_gained: 'met',
  scope_changed: 'not-applicable',
  evidence_changed: 'caution',
  not_comparable: 'not-evidenced',
  control_added: 'not-applicable',
  control_removed: 'not-applicable',
};

/** What each category means, in the words a customer reads. */
const KIND_MEANING: Record<string, string> = {
  regression: 'Met at the previous scan, and not met now.',
  improvement: 'Not met at the previous scan, and met now.',
  visibility_lost:
    'We could assess these before and cannot now — usually a licence lapsing or consent being ' +
    'withdrawn. This is not a regression: nothing here says the tenant got worse, only that we ' +
    'stopped being able to see it.',
  visibility_gained: 'We could not assess these before and can now.',
  scope_changed: 'These started or stopped applying to this tenant. Not better or worse.',
  evidence_changed:
    'Same verdict, different configuration underneath — the early warning. Still met, but ' +
    'something moved. The before and after values are shown for each.',
  not_comparable:
    'Our own rules, thresholds or collection changed between the two scans, so any difference in ' +
    'verdict may be ours rather than the customer’s. Read the reason on each before drawing a ' +
    'conclusion.',
  control_added: 'Added to the catalogue since the previous scan, so there is nothing to compare.',
  control_removed: 'Retired from the catalogue since the previous scan.',
};

/** Categories whose lead needs to be read, not skimmed past. */
const LEAD_CALLOUT: Record<string, string> = {
  visibility_lost: 'callout callout-evidence',
  not_comparable: 'callout callout-evidence',
  evidence_changed: 'callout callout-caution',
};

const SEVERITY_RANK: Record<Severity, number> = { critical: 0, high: 1, medium: 2, low: 3 };

/** The five figures worth a headline. The rest are visible as their own groups. */
const TALLY: ReadonlyArray<{ key: string; label: string; rail: string; hint: string }> = [
  {
    key: 'regressions',
    label: 'Regressed',
    rail: 'not-met',
    hint: 'met before, not met now',
  },
  { key: 'improvements', label: 'Improved', rail: 'met', hint: 'not met before, met now' },
  {
    key: 'evidenceChanged',
    label: 'Changed, still met',
    rail: 'caution',
    hint: 'same verdict, different configuration',
  },
  {
    key: 'visibilityLost',
    label: 'No longer visible',
    rail: 'not-evidenced',
    hint: 'we stopped being able to see, not a regression',
  },
  {
    key: 'notComparable',
    label: 'Not comparable',
    rail: 'not-evidenced',
    hint: 'our rules changed, not necessarily the tenant',
  },
];

const bySeverityThenId = (a: DriftChange, b: DriftChange): number =>
  SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] || a.controlId.localeCompare(b.controlId);

const formatDay = (iso: string): string => {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleDateString('en-GB');
};

const kindLabel = (kind: string): string => KIND_LABEL[kind] ?? kind;

const barLength = (count: number): CSSProperties => ({ '--n': count }) as CSSProperties;

interface DriftProps {
  drift: DriftReport;
}

/**
 * What changed between two scans.
 *
 * The distinction this component exists to preserve is that a verdict can move
 * for three different reasons: the tenant changed, our visibility changed, or
 * *we* changed. Presenting all three as "regressions" is the drift equivalent
 * of a wrong FAIL — so each has its own group, its own explanation, its own
 * colour family, and in the last case the reason is printed on every affected
 * control.
 */
export const Drift = ({ drift }: DriftProps) => {
  const grouped = KIND_ORDER.map((kind) => ({
    kind,
    changes: drift.changes.filter((change) => change.kind === kind).sort(bySeverityThenId),
  })).filter((group) => group.changes.length > 0);

  // A kind this console does not recognise still has to appear. The server can
  // be a version ahead of the bundle, and silently dropping a change would be
  // the one failure mode a drift report cannot have.
  const unrecognised = drift.changes
    .filter((change) => !KIND_ORDER.includes(change.kind))
    .sort(bySeverityThenId);

  const before = formatDay(drift.from.completedAt);
  const after = formatDay(drift.to.completedAt);

  return (
    <div className="dw-results">
      {drift.comparabilityNotes.length > 0 && (
        <div className="callout callout-caution dw-note">
          <span className="callout-title">Read these differences with care</span>
          <ul className="dw-plain-list">
            {drift.comparabilityNotes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Both scans are historical artefacts; neither is live, and the wording
          says so by naming the two dates rather than saying "now". */}
      <p className="note dw-interval">
        Comparing the scan of <span className="num">{before}</span> with the scan of{' '}
        <span className="num">{after}</span> — {drift.intervalDays}{' '}
        {drift.intervalDays === 1 ? 'day' : 'days'} later.
      </p>

      {drift.identicalSnapshot && (
        <div className="empty">
          <span className="empty-title">Nothing changed</span>
          <p className="empty-body">
            The collected configuration is identical to the previous scan, down to the snapshot
            hash. No control changed verdict and no evidence moved.
          </p>
        </div>
      )}

      {!drift.identicalSnapshot && drift.changes.length === 0 && (
        <div className="empty">
          <span className="empty-title">No control changed</span>
          <p className="empty-body">
            The tenant configuration differs from the previous scan, but nothing that any control in
            the catalogue measures has moved.
          </p>
        </div>
      )}

      {drift.changes.length > 0 && (
        <>
          <div className="stat-grid">
            {TALLY.map((entry) => {
              const count = drift.summary[entry.key] ?? 0;
              return (
                <div key={entry.key} className="stat stat-tile">
                  <span className="stat-figure">{count}</span>
                  <span
                    className={`dw-bar dw-bar-${entry.rail}`}
                    style={barLength(count)}
                    aria-hidden="true"
                  />
                  <span className="stat-label">{entry.label}</span>
                  <span className="stat-hint">{entry.hint}</span>
                </div>
              );
            })}
          </div>

          {grouped.map((group) => (
            <DriftSection
              key={group.kind}
              kind={group.kind}
              changes={group.changes}
              before={before}
              after={after}
            />
          ))}

          {unrecognised.length > 0 && (
            <section className="dw-section" aria-labelledby="drift-other">
              <h3 id="drift-other" className="dw-section-heading">
                <span className="dw-kind">Other changes</span>
                <span className="dw-section-count num">
                  {unrecognised.length} {unrecognised.length === 1 ? 'control' : 'controls'}
                </span>
              </h3>
              <p className="note dw-section-lead">
                Reported by a newer version of the assessment engine than this console recognises.
                The verdicts and evidence below are shown as received.
              </p>
              {unrecognised.map((change) => (
                <ChangeCard key={change.controlId} change={change} before={before} after={after} />
              ))}
            </section>
          )}
        </>
      )}
    </div>
  );
};

interface SectionProps {
  kind: string;
  changes: DriftChange[];
  before: string;
  after: string;
}

const DriftSection = ({ kind, changes, before, after }: SectionProps) => (
  <section className="dw-section" aria-labelledby={`drift-${kind}`}>
    <h3 id={`drift-${kind}`} className="dw-section-heading">
      <span className={`dw-kind dw-kind-${KIND_RAIL[kind] ?? 'not-applicable'}`}>
        {kindLabel(kind)}
      </span>
      <span className="dw-section-count num">
        {changes.length} {changes.length === 1 ? 'control' : 'controls'}
      </span>
    </h3>
    <p className={`${LEAD_CALLOUT[kind] ?? 'note'} dw-section-lead`}>{KIND_MEANING[kind] ?? ''}</p>
    {changes.map((change) => (
      <ChangeCard key={change.controlId} change={change} before={before} after={after} />
    ))}
  </section>
);

interface SideProps {
  caption: string;
  when: string;
  at: DriftChange['before'];
}

const Side = ({ caption, when, at }: SideProps) => (
  <div className="dw-side">
    <span className="label">
      {caption} · <span className="num">{when}</span>
    </span>
    {at ? (
      <span className={`verdict verdict-${VERDICT_KEY[at.verdict]}`}>
        {VERDICT_LABEL[at.verdict]}
      </span>
    ) : (
      <span className="dw-absent">Not in this scan</span>
    )}
    {at?.reasonCode && (
      <span className="note dw-side-reason">{REASON_LABEL[at.reasonCode] ?? at.reasonCode}</span>
    )}
  </div>
);

interface ChangeCardProps {
  change: DriftChange;
  before: string;
  after: string;
}

const ChangeCard = ({ change, before, after }: ChangeCardProps) => {
  const rail = KIND_RAIL[change.kind] ?? 'not-applicable';

  return (
    <article className={`card dw-finding dw-rail-${rail}`}>
      <div className="card-head dw-finding-head">
        <div className="dw-ident">
          <span className="label mono dw-control-id">{change.controlId}</span>
          <h4 className="section-title">{change.title}</h4>
        </div>
        <div className="dw-marks">
          <Sev severity={change.severity} />
        </div>
      </div>

      <div className="card-body">
        {change.incomparableReason && (
          <div className="callout callout-caution dw-note">
            <span className="callout-title">Why these two scans cannot be compared</span>
            {change.incomparableReason}
          </div>
        )}

        <div className="dw-transition">
          <Side caption="Previous scan" when={before} at={change.before} />
          <span className="dw-arrow" aria-hidden="true" />
          <Side caption="This scan" when={after} at={change.after} />
        </div>

        {change.evidenceChanges.length > 0 && (
          <div className="tbl-wrap dw-diff-wrap">
            <table className="tbl dw-diff">
              <caption className="sr-only">Evidence that moved for {change.controlId}</caption>
              <thead>
                <tr>
                  <th scope="col">Evidence</th>
                  <th scope="col">Previous scan · {before}</th>
                  <th scope="col">This scan · {after}</th>
                  <th scope="col">What moved</th>
                </tr>
              </thead>
              <tbody>
                {change.evidenceChanges.map((item) => (
                  <tr key={item.label}>
                    <th scope="row">
                      <span className="dw-pair-label">{item.label}</span>
                      <span className="dw-evclass">{item.class}</span>
                    </th>
                    <td>
                      <EvidenceValues values={item.before} />
                    </td>
                    <td>
                      <EvidenceValues values={item.after} />
                    </td>
                    <td>
                      {/* Added and removed are told apart by a sign and a word,
                          and by presence against recession — not by green
                          against red. Neither direction has a fixed valence: a
                          removed exclusion is an improvement and a removed
                          policy is a regression, and the diff cannot know
                          which. Colouring it would assert a verdict the rules
                          engine never reached. */}
                      {item.added.length === 0 && item.removed.length === 0 && (
                        <span className="dw-void">—</span>
                      )}
                      {item.added.length > 0 && (
                        <div className="dw-moved dw-added">
                          <span className="label">Added</span>
                          <EvidenceValues values={item.added} />
                        </div>
                      )}
                      {item.removed.length > 0 && (
                        <div className="dw-moved dw-removed">
                          <span className="label">Removed</span>
                          <EvidenceValues values={item.removed} />
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </article>
  );
};
